import {OTP, SSU} from '@edume/bento/auth';

import config from '../config';

const baseUrl = () => config.lfeRoot || 'https://learn.edume.com';

// https://github.com/danharper/hmac-examples?tab=readme-ov-file#javascript-es6
const hmac256 = async (message: string, key: string) => {
  const getUtf8Bytes = (str: string) =>
    new Uint8Array(
      [...unescape(encodeURIComponent(str))].map((c) => c.charCodeAt(0))
    );

  const keyBytes = getUtf8Bytes(key);
  const messageBytes = getUtf8Bytes(message);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    {name: 'HMAC', hash: 'SHA-256'},
    true,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, messageBytes);

  return [...new Uint8Array(sig)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

interface AccessLinkArgs {
  groupOrTeamIds: number | number[];
  moduleId?: number;
  lessonId?: number;
  guideId?: number;
  knowledgeHubId?: number;
  knowledgeHubSectionId?: number;
}

const createContentAccessLink = ({
  urlStart,
  groupOrTeamIds,
  moduleId,
  lessonId,
  guideId,
  knowledgeHubId,
  knowledgeHubSectionId,
}: AccessLinkArgs & {urlStart: string}): string => {
  let contentPath = '';
  if (moduleId) {
    contentPath = `/m/${moduleId}`;
  } else if (lessonId) {
    contentPath = `/l/${lessonId}`;
  } else if (guideId) {
    contentPath = `/gd/${guideId}`;
  } else if (knowledgeHubId) {
    contentPath = `/kh/${knowledgeHubId}`;
    if (knowledgeHubSectionId) {
      contentPath += `/khs/${knowledgeHubSectionId}`;
    }
  }
  return `${urlStart}${contentPath}?g=${groupOrTeamIds}`;
};

export const createOtpLink = (args: AccessLinkArgs) =>
  createContentAccessLink({urlStart: `${baseUrl()}/otp`, ...args});

export const createSsuSsoLink = (args: AccessLinkArgs) =>
  createContentAccessLink({urlStart: `${baseUrl()}/w`, ...args});

export const createContentSeamlessBaseLink = (
  contentType: string,
  contentId: number,
  teamId: number
) => {
  switch (contentType) {
    case 'course':
      return createCourseSeamlessBaseLink(contentId, teamId);
    case 'guide':
      return createGuideSeamlessBaseLink(contentId, teamId);
    default:
      throw Error('contentType not supported');
  }
};

export const createCourseSeamlessBaseLink = (
  courseId: number,
  teamId: number
) => `${baseUrl()}/e/c/${courseId}?g=${teamId}`;

export const createModuleSeamlessBaseLink = (
  moduleId: number,
  teamId: number
) => `${baseUrl()}/e/m/${moduleId}?g=${teamId}`;

export const createKnowledgeHubSeamlessBaseLink = (
  knowledgeHubId: number,
  teamIds: number | number[],
  knowledgeHubSectionId?: number
) =>
  `${baseUrl()}/e/kh/${knowledgeHubId}${
    knowledgeHubSectionId ? `/khs/${knowledgeHubSectionId}` : ''
  }?g=${teamIds}`;

export const createGuideSeamlessBaseLink = (guideId: number, teamId: number) =>
  `${baseUrl()}/e/gd/${guideId}?g=${teamId}`;

export const createGuideAnonymousSeamlessBaseLink = (
  guideId: number,
  teamId: number
) => `${baseUrl()}/e/gd/${guideId}?g=${teamId}&anon=true`;

export const generateSeamlessLink = async (url: string, secret: string) => {
  const generateSignature = await hmac256(url, secret);

  return url + '&sig=' + generateSignature;
};

interface KnowledgeHubLinkArgs {
  type: typeof SSU | typeof OTP | 'seamless';
  knowledgeHubId: number;
  groupOrTeamIds: number | number[];
  knowledgeHubSectionId?: number;
}

export const getKnowledgeHubLink = ({
  type,
  knowledgeHubId,
  knowledgeHubSectionId,
  groupOrTeamIds,
}: KnowledgeHubLinkArgs): string => {
  switch (type) {
    case SSU:
      return createSsuSsoLink({
        knowledgeHubId,
        groupOrTeamIds,
        knowledgeHubSectionId,
      });
    case OTP:
      return createOtpLink({
        knowledgeHubId,
        groupOrTeamIds,
        knowledgeHubSectionId,
      });
    case 'seamless':
      return createKnowledgeHubSeamlessBaseLink(
        knowledgeHubId,
        groupOrTeamIds,
        knowledgeHubSectionId
      );
    default:
      return '';
  }
};

export const createPublicUploaderLink = (linkCode: string) =>
  `${baseUrl()}/pu/${linkCode}`;
